import * as THREE from 'three';
import { DECOY, type MissileParams } from '../config/weapons';
import type { Rng } from '../core/Rng';
import type { Terrain } from '../world/Terrain';
import { COUNTERMEASURES } from '../config/tuning';
import { chaffRadiusM, DecoyKind, flareIntensity, type Decoy } from './Countermeasures';
import type { RadarTarget } from './Detection';

/**
 * Seeker models (SPEC §3.6.3) — the layer where the player's evasion techniques actually bite.
 *
 * Each seeker type is defeated by a different thing, on purpose, so that no single technique
 * solves the game:
 *
 *  - **radar-command** rides the launching site's track. Notch the *site*, mask it behind
 *    terrain, or break its lock with chaff, and the missile goes blind.
 *  - **radar-active** carries its own radar for the terminal phase, so notching the ground site
 *    is no longer enough once it has gone active — but chaff and terrain still work.
 *  - **ir** ignores the notch entirely. A player who has learned only notching will die to a
 *    MANPADS, and SPEC §3.6.3 is explicit that this is intentional pedagogy.
 *
 * Lock loss does not make a missile harmless. It coasts on the last known vector for a memory
 * period and then flies ballistic — still fused, still lethal if the player flies into it.
 *
 * Randomness is drawn from a seeded stream owned by the missile, never `Math.random`: a decoy
 * that worked in one replay and not the next would make the debrief of SPEC §3.6.5 worthless.
 */

export const LockState = {
  /** Guiding on a live track. */
  Locked: 'locked',
  /** Lock lost; coasting on the last known target vector. */
  Coasting: 'coasting',
  /** Memory expired, or decoyed away: flying a fixed vector, blind but still fused. */
  Ballistic: 'ballistic',
} as const;
export type LockStateId = (typeof LockState)[keyof typeof LockState];

/** Why a seeker lost its lock. Recorded for the debrief (SPEC §3.6.5). */
export const LockLossCause = {
  None: 'none',
  Notch: 'notch',
  TerrainMask: 'terrain',
  Chaff: 'chaff',
  Flare: 'flare',
  SeekerFov: 'fov',
  SiteLost: 'site',
} as const;
export type LockLossCauseId = (typeof LockLossCause)[keyof typeof LockLossCause];

/**
 * What the seeker is being asked to look at this tick.
 *
 * Owned and reused by the caller: the target, terrain and decoy list are the same for every
 * missile in the air, so they are filled in once per tick rather than per missile, and each
 * missile only writes its own three fields. One object, no allocation (SPEC §4.3).
 */
export interface SeekerContext {
  /** Set by the missile. Held by reference, not copied. */
  missilePositionM: THREE.Vector3;
  missileVelocityMs: THREE.Vector3;
  /** True once the missile has entered its terminal phase, when an active seeker goes live. */
  terminal: boolean;
  /** True while the launching site still holds its own track (command guidance depends on it). */
  siteHasTrack: boolean;

  // --- Set by the caller, shared across every missile in flight.
  target: RadarTarget;
  decoys: readonly Decoy[];
  terrain: Terrain;
}

/** A context with the shared fields supplied; the missile fills in the rest each tick. */
export function createSeekerContext(target: RadarTarget, terrain: Terrain): SeekerContext {
  return {
    missilePositionM: new THREE.Vector3(),
    missileVelocityMs: new THREE.Vector3(),
    terminal: false,
    siteHasTrack: false,
    target,
    decoys: [],
    terrain,
  };
}

const toTarget = new THREE.Vector3();
const toDecoy = new THREE.Vector3();
const missileForward = new THREE.Vector3();

/**
 * Half-angle between two directions from the missile, radians. The number the flare rule turns on.
 */
export function angularSeparationRad(
  fromM: THREE.Vector3,
  aM: THREE.Vector3,
  bM: THREE.Vector3,
): number {
  toTarget.copy(aM).sub(fromM);
  toDecoy.copy(bM).sub(fromM);
  const lengths = toTarget.length() * toDecoy.length();
  if (lengths < 1e-9) return 0;
  const cos = toTarget.dot(toDecoy) / lengths;
  return Math.acos(cos > 1 ? 1 : cos < -1 ? -1 : cos);
}

/** True when a point lies inside the seeker's field-of-view cone about the missile's nose. */
export function withinSeekerFov(
  missilePositionM: THREE.Vector3,
  missileVelocityMs: THREE.Vector3,
  pointM: THREE.Vector3,
  fovDeg: number,
): boolean {
  const speed = missileVelocityMs.length();
  if (speed < 1e-6) return true;
  missileForward.copy(missileVelocityMs).divideScalar(speed);
  toTarget.copy(pointM).sub(missilePositionM);
  const range = toTarget.length();
  if (range < 1e-6) return true;
  const cos = toTarget.dot(missileForward) / range;
  return cos >= Math.cos((fovDeg * Math.PI) / 180);
}

/**
 * IR aspect factor, unitless: how well the seeker sees the airframe from where it is.
 *
 * A v1 seeker looks for an exhaust plume, so it sees a receding target far better than an
 * approaching one. `rearAspectBias` below 1 is what makes "deny it the tail" a real technique and
 * a head-on shot a poor one.
 */
export function irAspectFactor(
  missilePositionM: THREE.Vector3,
  target: RadarTarget,
  rearAspectBias: number,
): number {
  toTarget.copy(missilePositionM).sub(target.positionM);
  const range = toTarget.length();
  if (range < 1e-6) return 1;
  // +1 when the missile is directly astern of the target, −1 when it is dead ahead.
  const alignment = toTarget.dot(target.forward) / range;
  const rearness = (1 - alignment) * 0.5;
  return rearAspectBias + (1 - rearAspectBias) * rearness;
}

export class Seeker {
  state: LockStateId = LockState.Locked;
  lossCause: LockLossCauseId = LockLossCause.None;

  /** Last known target position, flown toward while coasting. */
  readonly lastKnownM = new THREE.Vector3();
  readonly lastKnownVelocityMs = new THREE.Vector3();

  /** Position the seeker is currently guiding on — the target, or a decoy it has been fooled by. */
  readonly aimPointM = new THREE.Vector3();
  readonly aimVelocityMs = new THREE.Vector3();

  /** Seconds since the lock was lost. */
  coastS = 0;
  /** The decoy the seeker transferred to, if any. */
  decoyed: Decoy | null = null;

  /** Rebound on every launch, so a pooled missile never allocates a new seeker. */
  private params: MissileParams;

  constructor(params: MissileParams) {
    this.params = params;
  }

  /** Point a recycled seeker at a different round's parameters. */
  configure(params: MissileParams): void {
    this.params = params;
    this.reset();
  }

  reset(): void {
    this.state = LockState.Locked;
    this.lossCause = LockLossCause.None;
    this.coastS = 0;
    this.decoyed = null;
    this.lastKnownM.set(0, 0, 0);
    this.lastKnownVelocityMs.set(0, 0, 0);
    this.aimPointM.set(0, 0, 0);
    this.aimVelocityMs.set(0, 0, 0);
  }

  /**
   * Advance the seeker and write the point the missile should guide on into `aimPointM`.
   *
   * `memoryS` is how long the seeker coasts on the last known vector before going ballistic.
   */
  update(dtS: number, context: SeekerContext, rng: Rng, memoryS: number): void {
    if (this.state === LockState.Ballistic) return;

    // A seeker already fooled by a decoy guides on it until the decoy burns out, then is blind.
    if (this.decoyed !== null) {
      if (this.decoyed.active) {
        this.aimPointM.copy(this.decoyed.positionM);
        this.aimVelocityMs.copy(this.decoyed.velocityMs);
        return;
      }
      this.decoyed = null;
      this.goBallistic();
      return;
    }

    const holdsLock = this.evaluateLock(dtS, context, rng);

    if (holdsLock) {
      this.state = LockState.Locked;
      this.coastS = 0;
      this.lossCause = LockLossCause.None;
      this.lastKnownM.copy(context.target.positionM);
      this.lastKnownVelocityMs.copy(context.target.velocityMs);
      this.aimPointM.copy(context.target.positionM);
      this.aimVelocityMs.copy(context.target.velocityMs);
      return;
    }

    // Lost: coast on the last known vector, extrapolating it forward. This is what gives a
    // well-timed break its payoff — the missile keeps flying at where the aircraft *was*.
    this.state = LockState.Coasting;
    this.coastS += dtS;
    if (this.coastS >= memoryS) {
      this.goBallistic();
      return;
    }
    this.lastKnownM.addScaledVector(this.lastKnownVelocityMs, dtS);
    this.aimPointM.copy(this.lastKnownM);
    this.aimVelocityMs.copy(this.lastKnownVelocityMs);
  }

  private goBallistic(): void {
    this.state = LockState.Ballistic;
    this.aimVelocityMs.set(0, 0, 0);
  }

  /** All the ways a lock can be broken, in the order they are cheapest to test. */
  private evaluateLock(dtS: number, context: SeekerContext, rng: Rng): boolean {
    const params = this.params;
    const target = context.target;

    // Range: past the seeker's reach, nothing else matters.
    const rangeM = context.missilePositionM.distanceTo(target.positionM);
    if (rangeM > params.seekerLockRangeM) {
      this.lossCause = LockLossCause.SeekerFov;
      return false;
    }

    if (params.seeker === 'ir') {
      return this.evaluateInfrared(dtS, context, rng);
    }
    return this.evaluateRadar(dtS, context, rng);
  }

  /**
   * Radar seekers: command guidance rides the site's track; an active seeker takes over in the
   * terminal phase. Terrain masking and chaff defeat both.
   */
  private evaluateRadar(dtS: number, context: SeekerContext, rng: Rng): boolean {
    const params = this.params;
    const active = params.seeker === 'radar-active' && context.terminal;

    // Command guidance is only as good as the site's own track: notch the site, mask it, or
    // out-range it and the missile is taking instructions from someone who cannot see.
    if (!active && !context.siteHasTrack) {
      this.lossCause = LockLossCause.SiteLost;
      return false;
    }

    // An active seeker has to see the target itself — terrain between them breaks it.
    if (
      !context.terrain.lineOfSight(context.missilePositionM, context.target.positionM)
    ) {
      this.lossCause = LockLossCause.TerrainMask;
      return false;
    }

    if (this.chaffDefeats(dtS, context, rng)) {
      this.lossCause = LockLossCause.Chaff;
      return false;
    }
    return true;
  }

  /**
   * Chaff: a cloud only masks the return while it sits between the missile and the aircraft.
   *
   * Probability accrues per second inside that geometry rather than firing once on release, so a
   * cloud punched early and left behind does nothing, and one placed across the missile's line
   * with a turn behind it does a great deal.
   */
  private chaffDefeats(dtS: number, context: SeekerContext, rng: Rng): boolean {
    const susceptibility = this.params.chaffSusceptibility;
    if (susceptibility <= 0) return false;

    const maxSeparation = (DECOY.chaffMaxSeparationDeg * Math.PI) / 180;
    for (let i = 0; i < context.decoys.length; i += 1) {
      const decoy = context.decoys[i];
      if (decoy.kind !== DecoyKind.Chaff || !decoy.active) continue;

      const separation = angularSeparationRad(
        context.missilePositionM,
        context.target.positionM,
        decoy.positionM,
      );
      if (separation > maxSeparation) continue;

      // Only a bloomed cloud is a cloud. A bundle still opening is barely a return at all.
      const bloom = chaffRadiusM(decoy.ageS) / COUNTERMEASURES.chaffRadiusM;
      const probability = DECOY.chaffDefeatPerS * susceptibility * bloom * dtS;
      if (rng.chance(probability)) {
        this.decoyed = decoy;
        this.aimPointM.copy(decoy.positionM);
        this.aimVelocityMs.copy(decoy.velocityMs);
        return true;
      }
    }
    return false;
  }

  /**
   * Infrared: track the hottest source in the cone, with a rear-aspect bias.
   *
   * The flare rule is the important one (SPEC §3.6.3): a flare is only a candidate once it has
   * separated from the aircraft by more than `flareTurnRequiredDeg` *as seen from the missile*.
   * Release and fly straight and the flare stays inside your own signature — the angle never
   * opens, and nothing happens. Release and turn and the two sources part company, and the
   * seeker takes the brighter one. That is the entire reason flares alone do not work.
   */
  private evaluateInfrared(dtS: number, context: SeekerContext, rng: Rng): boolean {
    const params = this.params;

    if (
      !withinSeekerFov(
        context.missilePositionM,
        context.missileVelocityMs,
        context.target.positionM,
        params.seekerFovDeg,
      )
    ) {
      this.lossCause = LockLossCause.SeekerFov;
      return false;
    }

    const aircraftHeat = irAspectFactor(
      context.missilePositionM,
      context.target,
      params.rearAspectBias ?? 1,
    );

    const required = (COUNTERMEASURES.flareTurnRequiredDeg * Math.PI) / 180;
    for (let i = 0; i < context.decoys.length; i += 1) {
      const decoy = context.decoys[i];
      if (decoy.kind !== DecoyKind.Flare || !decoy.active) continue;
      if (
        !withinSeekerFov(
          context.missilePositionM,
          context.missileVelocityMs,
          decoy.positionM,
          params.seekerFovDeg,
        )
      ) {
        continue;
      }

      const separation = angularSeparationRad(
        context.missilePositionM,
        context.target.positionM,
        decoy.positionM,
      );
      if (separation < required) continue;

      const heat = flareIntensity(decoy.ageS);
      if (heat <= aircraftHeat) continue;

      const probability =
        DECOY.flareDefeatPerS * params.flareSusceptibility * (heat / (heat + aircraftHeat)) * dtS;
      if (rng.chance(probability)) {
        this.decoyed = decoy;
        this.aimPointM.copy(decoy.positionM);
        this.aimVelocityMs.copy(decoy.velocityMs);
        this.lossCause = LockLossCause.Flare;
        return false;
      }
    }

    // Terrain between the missile and a heat source hides it just as well as it hides a radar
    // return; and a target seen only from the front by a v1 seeker may be too cold to hold.
    if (!context.terrain.lineOfSight(context.missilePositionM, context.target.positionM)) {
      this.lossCause = LockLossCause.TerrainMask;
      return false;
    }
    return true;
  }
}
